import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Zap, Trophy } from "lucide-react";

const ROUNDS = 5;

export function GameReaction({ onScoreSubmit }) {
    const [state, setState] = useState("idle"); // idle | waiting | ready | tooSoon | done
    const [round, setRound] = useState(0);
    const [times, setTimes] = useState([]);
    const [best, setBest] = useState(() => parseInt(localStorage.getItem("crm_reaction_best") || "0", 10));
    const startRef = useRef(0);
    const timerRef = useRef(null);
    const submitted = useRef(false);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const startRound = () => {
        setState("waiting");
        const delay = 1500 + Math.random() * 2500;
        timerRef.current = setTimeout(() => {
            setState("ready");
            startRef.current = performance.now();
        }, delay);
    };

    const handleClick = () => {
        if (state === "idle" || state === "done") {
            // Start fresh game
            setRound(0);
            setTimes([]);
            submitted.current = false;
            startRound();
            return;
        }
        if (state === "waiting") {
            clearTimeout(timerRef.current);
            setState("tooSoon");
            return;
        }
        if (state === "tooSoon") {
            startRound();
            return;
        }
        if (state === "ready") {
            const elapsed = Math.round(performance.now() - startRef.current);
            const newTimes = [...times, elapsed];
            setTimes(newTimes);
            if (newTimes.length >= ROUNDS) {
                setState("done");
                // Score = inverse of avg (lower ms = higher score). Cap.
                const avg = newTimes.reduce((s, n) => s + n, 0) / newTimes.length;
                const score = Math.max(0, Math.round(10000 / Math.max(1, avg) * 100));
                if (avg < best || best === 0) {
                    setBest(Math.round(avg));
                    localStorage.setItem("crm_reaction_best", String(Math.round(avg)));
                }
                if (!submitted.current) {
                    submitted.current = true;
                    onScoreSubmit?.({ score, durationMs: newTimes.reduce((s, n) => s + n, 0) });
                }
            } else {
                setRound(round + 1);
                setState("waiting");
                const delay = 1500 + Math.random() * 2500;
                timerRef.current = setTimeout(() => {
                    setState("ready");
                    startRef.current = performance.now();
                }, delay);
            }
        }
    };

    const bg = {
        idle: "bg-blue-600 hover:bg-blue-700",
        waiting: "bg-red-600",
        ready: "bg-emerald-500 hover:bg-emerald-600",
        tooSoon: "bg-amber-500 hover:bg-amber-600",
        done: "bg-blue-600 hover:bg-blue-700"
    }[state];

    const msg = {
        idle: "Click to start",
        waiting: "Wait for green...",
        ready: "CLICK NOW!",
        tooSoon: "Too soon! Click to retry",
        done: "Done — click to restart"
    }[state];

    const avg = times.length ? Math.round(times.reduce((s, n) => s + n, 0) / times.length) : 0;

    return (
        <div className="flex flex-col items-center w-full">
            <div className="flex gap-2 mb-3">
                <ScoreBox label="Round" value={`${Math.min(times.length + (state === "done" ? 0 : 1), ROUNDS)}/${ROUNDS}`} />
                <ScoreBox label="Best avg" value={best ? `${best}ms` : "-"} accent />
                {avg > 0 && <ScoreBox label="Avg" value={`${avg}ms`} />}
            </div>

            <button
                onClick={handleClick}
                className={`${bg} text-white font-bold text-2xl rounded-2xl shadow-lg transition-colors w-full max-w-md py-24 cursor-pointer select-none`}
            >
                <Zap className="h-7 w-7 mx-auto mb-2" />
                {msg}
            </button>

            {times.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
                    {times.map((t, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-gray-100 rounded-md tabular-nums font-mono">
                            R{i + 1}: <b>{t}ms</b>
                        </span>
                    ))}
                </div>
            )}

            {state === "done" && (
                <div className="mt-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center max-w-md">
                    <Trophy className="h-5 w-5 text-emerald-600 inline mb-1" />
                    <div className="text-sm font-semibold text-emerald-800">
                        Average: {avg}ms · Best round: {Math.min(...times)}ms
                    </div>
                </div>
            )}
        </div>
    );
}

function ScoreBox({ label, value, accent }) {
    return (
        <div className={`px-3 py-1.5 rounded-md text-center ${accent ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-700"}`}>
            <div className="text-[10px] uppercase font-semibold tracking-wide opacity-70">{label}</div>
            <div className="text-sm font-bold tabular-nums">{value}</div>
        </div>
    );
}
